import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { proposalsApi, templatesApi, formatApiError } from "./api";
import {
  Plus, Search, Copy, Trash2, ExternalLink, LogOut, MapPin, User, BookmarkPlus,
} from "lucide-react";

const STATUS_LABELS = {
  draft: "Borrador",
  sent: "Enviada",
  viewed: "Vista",
  accepted: "Aceptada",
  rejected: "Rechazada",
  booked: "Reservada",
};

const STATUS_COLORS = {
  draft: "bg-[#16335A]/10 text-[#16335A]",
  sent: "bg-blue-100 text-blue-800",
  viewed: "bg-purple-100 text-purple-800",
  accepted: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  booked: "bg-[#E8C97A]/30 text-[#16335A] font-semibold",
};

function StatusPill({ status }) {
  return (
    <span className={`inline-block px-3 py-1 text-xs rounded-full ${STATUS_COLORS[status] || STATUS_COLORS.draft}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter) params.status = statusFilter;
      const data = await proposalsApi.list(params);
      setProposals(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleDuplicate = async (id) => {
    try {
      const copy = await proposalsApi.duplicate(id);
      navigate(`/admin/proposals/${copy.id}`);
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar la propuesta "${name}"? No se puede deshacer.`)) return;
    try {
      await proposalsApi.remove(id);
      setProposals((ps) => ps.filter((p) => p.id !== id));
    } catch (err) { alert(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Top bar */}
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3" data-testid="admin-home">
            <div className="font-serif leading-tight">
              <div className="text-base">Escapafy</div>
              <div className="italic text-[#E8C97A] text-[0.65rem] tracking-[0.18em] uppercase">Admin</div>
            </div>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/admin/templates" data-testid="nav-templates" className="text-sm text-white/80 hover:text-[#E8C97A] hidden sm:inline">Plantillas</Link>
            <Link to="/admin/escapadas" data-testid="nav-escapadas" className="text-sm text-white/80 hover:text-[#E8C97A] hidden sm:inline">Escapadas</Link>
            <Link to="/admin/payments" data-testid="nav-payments" className="text-sm text-white/80 hover:text-[#E8C97A] hidden sm:inline">Pagos</Link>
            <Link to="/admin/affiliates" data-testid="nav-affiliates" className="text-sm text-white/80 hover:text-[#E8C97A] hidden sm:inline">Afiliados</Link>
            <Link to="/admin/stats" data-testid="nav-stats" className="text-sm text-white/80 hover:text-[#E8C97A] hidden sm:inline">Estadísticas</Link>
            <span className="hidden md:inline text-sm text-white/70">{user?.email}</span>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="text-sm text-white/70 hover:text-[#E8C97A] inline-flex items-center gap-2 transition-colors"
            >
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <span className="label-eyebrow text-[#16335A]/70">Propuestas de viaje</span>
            <h1 className="mt-2 font-display text-3xl lg:text-5xl text-[#0A192F] tracking-tight">
              Tus <span className="italic text-[#16335A]">propuestas</span>
            </h1>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-proposal-btn"
            className="bg-[#16335A] hover:bg-[#214A82] text-white px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors w-fit"
          >
            <Plus size={16} /> Nueva propuesta
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#16335A]/50" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por cliente, destino..."
              data-testid="proposals-search"
              className="w-full pl-11 pr-4 py-3 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
              style={{ borderRadius: "2px" }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="proposals-status-filter"
            className="px-4 py-3 bg-white border border-[#16335A]/15 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
        </div>

        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 mb-6 text-sm">{error}</p>}

        {/* Empty + loading */}
        {loading && <p className="text-[#3A506B] italic">Cargando...</p>}
        {!loading && proposals.length === 0 && (
          <div className="bg-white border border-[#16335A]/10 p-12 text-center" style={{ borderRadius: "2px" }}>
            <p className="font-display text-2xl text-[#0A192F]">No hay propuestas todavía</p>
            <p className="mt-2 text-[#3A506B]">Crea tu primera propuesta para empezar.</p>
            <button
              onClick={() => setShowCreate(true)}
              className="mt-6 bg-[#16335A] hover:bg-[#214A82] text-white px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2"
            >
              <Plus size={16} /> Crear propuesta
            </button>
          </div>
        )}

        {/* List */}
        {!loading && proposals.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {proposals.map((p) => (
              <div
                key={p.id}
                data-testid={`proposal-card-${p.id}`}
                className="bg-white border border-[#16335A]/10 hover:border-[#16335A]/30 transition-all group flex flex-col"
                style={{ borderRadius: "2px" }}
              >
                {/* Cover */}
                <Link
                  to={`/admin/proposals/${p.id}`}
                  className="relative aspect-[16/9] bg-[#16335A]/10 overflow-hidden"
                >
                  {p.cover_image ? (
                    <img src={p.cover_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#16335A]/30">
                      <MapPin size={32} />
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <StatusPill status={p.status} />
                  </div>
                </Link>
                {/* Body */}
                <Link to={`/admin/proposals/${p.id}`} className="p-5 flex-1">
                  <h3 className="font-display text-xl text-[#0A192F] leading-tight">
                    {p.trip_title || p.destination || "Sin destino"}
                  </h3>
                  <p className="mt-1 text-sm text-[#3A506B] flex items-center gap-2">
                    <User size={12} /> {p.client_name}
                  </p>
                  {(p.date_from || p.date_to) && (
                    <p className="mt-1 text-xs text-[#3A506B]">
                      {p.date_from} {p.date_to ? `→ ${p.date_to}` : ""}
                    </p>
                  )}
                </Link>
                {/* Footer */}
                <div className="border-t border-[#16335A]/10 px-5 py-3 flex items-center justify-between text-xs">
                  <span className="text-[#3A506B]/70">
                    {p.views || 0} {p.views === 1 ? "vista" : "vistas"}
                    {p.clicks ? ` · ${p.clicks} clicks` : ""}
                  </span>
                  <div className="flex items-center gap-2">
                    <a
                      href={`/p/${p.share_token}`}
                      target="_blank"
                      rel="noreferrer"
                      title="Ver pública"
                      className="text-[#16335A]/70 hover:text-[#16335A]"
                    >
                      <ExternalLink size={14} />
                    </a>
                    <button
                      onClick={() => handleDuplicate(p.id)}
                      title="Duplicar"
                      data-testid={`duplicate-${p.id}`}
                      className="text-[#16335A]/70 hover:text-[#16335A]"
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id, p.client_name)}
                      title="Eliminar"
                      data-testid={`delete-${p.id}`}
                      className="text-red-600/70 hover:text-red-700"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showCreate && (
        <CreateProposalModal
          onClose={() => setShowCreate(false)}
          onCreated={(p) => navigate(`/admin/proposals/${p.id}`)}
        />
      )}
    </div>
  );
}

function CreateProposalModal({ onClose, onCreated }) {
  const [clientName, setClientName] = useState("");
  const [destination, setDestination] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    templatesApi.list().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      let p;
      if (templateId) {
        p = await templatesApi.use(templateId, {
          client_name: clientName.trim(),
          destination: destination.trim() || undefined,
        });
      } else {
        p = await proposalsApi.create({ client_name: clientName.trim(), destination: destination.trim() });
      }
      onCreated(p);
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-md w-full p-8 space-y-5"
        style={{ borderRadius: "2px" }}
        data-testid="create-proposal-form"
      >
        <h2 className="font-display text-2xl text-[#0A192F]">Nueva propuesta</h2>
        <p className="text-sm text-[#3A506B]">Empieza con lo básico — el resto lo añades en el editor.</p>

        {templates.length > 0 && (
          <label className="block">
            <span className="block text-xs font-medium text-[#16335A] mb-2 uppercase tracking-wider inline-flex items-center gap-2">
              <BookmarkPlus size={12} /> Partir de una plantilla (opcional)
            </span>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              data-testid="create-template-select"
              className="w-full px-4 py-3 bg-white border border-[#16335A]/20 focus:border-[#16335A] focus:outline-none text-sm"
              style={{ borderRadius: "2px" }}
            >
              <option value="">— En blanco —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="block text-xs font-medium text-[#16335A] mb-2 uppercase tracking-wider">Cliente *</span>
          <input
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            required
            autoFocus
            data-testid="create-client-name"
            className="w-full px-4 py-3 bg-white border border-[#16335A]/20 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-[#16335A] mb-2 uppercase tracking-wider">Destino (opcional)</span>
          <input
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="Roma, París, Lisboa..."
            data-testid="create-destination"
            className="w-full px-4 py-3 bg-white border border-[#16335A]/20 focus:border-[#16335A] focus:outline-none text-sm"
            style={{ borderRadius: "2px" }}
          />
        </label>
        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 text-sm">{error}</p>}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-5 py-3 text-sm text-[#3A506B] hover:text-[#16335A]">Cancelar</button>
          <button
            type="submit"
            disabled={loading}
            data-testid="create-proposal-submit"
            className="bg-[#16335A] hover:bg-[#214A82] disabled:opacity-60 text-white px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2"
          >
            {loading ? "Creando..." : "Crear propuesta"}
          </button>
        </div>
      </form>
    </div>
  );
}
